import type {
    LandingPageTemplateResponse,
    SignInProvider,
} from "@/types/landing";
import {
    LeadFormButtonVariant,
    CallToActionButton,
} from "../CallToActionButton";
import { FormEventHandler, SyntheticEvent, useState } from "react";
import { useRouter } from "next/router";
import { FormFields } from "../FormFields";
import type { Site } from "@/types/sites";
import { setCookie } from "@/utils/cookies/client";
import { USER_LOCATION_COOKIE } from "@/constants";
import type { LocationInfo } from "@/utils/userInfo";

import {
    getRedirectUrl,
    handleFormSubmit,
    leadFormId,
} from "./formSubmitUtils";
import { UserCookie } from "@/types/nextjs";

type DynamicFormProps = ({
    site,
    landingPage,
    fromPopup,
    setIsModalOpen,
    onOfferPage,
    setLoaderIsOpen,
    userCookie,
}: {
    site: Site | null;
    landingPage: LandingPageTemplateResponse | null;
    fromPopup?: boolean;
    setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    onOfferPage?: boolean;
    setLoaderIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    userCookie: UserCookie;
}) => React.ReactElement;

const getUniqueProviders = (
    providers: SignInProvider[] | undefined
): SignInProvider[] => {
    if (!providers) return [];
    /**
     * ACD-16830: The Enumeration type in component in the CMS doesn't allow unique entries
     * In order to prevent duplicates we are using a Set here
     */
    const providerSet = new Set<string>();
    return providers
        .filter((provider) => {
            if (providerSet.has(provider.provider)) {
                return false;
            }
            providerSet.add(provider.provider);
            return true;
        })
        .map((provider, index) => ({
            id: index,
            provider: provider.provider.toLocaleLowerCase(),
            buttonTextDesktop: provider.buttonTextDesktop,
            buttonTextMobile: provider.buttonTextMobile,
        }));
};

export const DynamicForm: DynamicFormProps = ({
    site,
    landingPage,
    fromPopup,
    setIsModalOpen,
    onOfferPage,
    setLoaderIsOpen,
    userCookie,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedState, setSelectedState] = useState<string>('');

    const router = useRouter();

    const redirectPrefetchUrl = getRedirectUrl(site, landingPage, true);

    if (redirectPrefetchUrl) {
        router.prefetch(redirectPrefetchUrl);
    }

    const {
        callToActionButtonText = "",
        sanitisedcallToActionButtonPrivacyText = "",
    } = landingPage?.data?.attributes ?? {};
    const formFields = landingPage?.data?.attributes?.sanitised_form_fields;

    const skipButtonText = landingPage?.data?.attributes?.skipLead?.buttonText;
    const providers = getUniqueProviders(
        landingPage?.data?.attributes?.signInProviders
    );

    // form submit
    const handleSubmit: FormEventHandler<HTMLFormElement> = async (
        e: SyntheticEvent<HTMLFormElement, SubmitEvent>
    ) => {
        e.preventDefault();

        setIsSubmitting(true);
        setLoaderIsOpen && setLoaderIsOpen(true);

        if (selectedState) {
            const locationInfo: LocationInfo = {
                region: selectedState,
                country: 'US',
                city: null
            };
            setCookie(USER_LOCATION_COOKIE, JSON.stringify(locationInfo), 60000 * 60 * 24 * 30);
        }

        const offerPageRedirectPath = await handleFormSubmit(
            e,
            site,
            landingPage,
            false,
            userCookie
        );

        if (offerPageRedirectPath && !onOfferPage) {
            await router.push({
                pathname: offerPageRedirectPath,
                query: router.query,
            });
        } else {
            setIsModalOpen && setIsModalOpen(false);
        }
    };

    return (
        <form id={leadFormId} onSubmit={handleSubmit}>
            {formFields?.data && formFields.data.length > 0 ? (
                <FormFields
                    formFields={formFields}
                    buttonText={callToActionButtonText}
                    fromPopup={fromPopup}
                    providers={providers}
                    skipButtonText={skipButtonText}
                    site={site}
                    landingPage={landingPage}
                    onOfferPage={onOfferPage}
                    isLoading={isSubmitting}
                    onStateChange={setSelectedState}
                />
            ) : (
                <CallToActionButton
                    buttonText={callToActionButtonText}
                    buttonPrivacyText={sanitisedcallToActionButtonPrivacyText}
                    variant={LeadFormButtonVariant.fullWidthWithoutFields}
                    isLoading={isSubmitting}
                />
            )}
        </form>
    );
};
